import { useId } from 'react';
import LabNoteBlock from './LabNoteBlock';
import {
  labBar, labCardLayout, labHiddenCopy, LAB_HEADER, LAB_PROVENANCE, LAB_FOOTER, LAB_OUTSIDE_NOTE, LAB_PENDING_LINE,
  type LabCardModel, type LabMeasuredRow,
} from '../logic/labResults';

const tone = (row: LabMeasuredRow) => (row.flag === 'in range' ? 'in' : 'outside');
const notesOf = (row: LabMeasuredRow) => [
  row.comment ? 'Lab comment' : null,
  row.corrected ? 'Corrected by the lab' : null,
].filter(Boolean).join(' · ');

// WF72's lab_results chat row as the patient's card, denser. The lab's flag is the only verdict
// shown: no goal line, since the chat panel holds no care plan and must not fetch one.
// `embedded`: the bubble sits on --wi-background-primary, so the range dot's ring follows it.
export default function LabResultsCard({ model, embedded = false }: { model: LabCardModel; embedded?: boolean }) {
  const headingId = useId();
  const {
    open, folded, noValue, foldedCount,
  } = labCardLayout(model);
  const meta = [model.collected ? `Collected ${model.collected}` : null, LAB_PROVENANCE.replace(/\.$/, '')]
    .filter(Boolean).join(' · ');
  const moreLabel = [
    foldedCount ? `Everything else in range (${foldedCount})` : null,
    noValue.length ? `${noValue.length} with no value from the lab` : null,
  ].filter(Boolean).join(' · ');

  return (
    <article className={`lab-card${embedded ? ' lab-card--embedded' : ''}`} aria-labelledby={headingId}>
      <header className="lab-card__head">
        <span className="lab-card__icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3h6" />
            <path d="M10 3v6.2L4.8 18.4A1.7 1.7 0 0 0 6.3 21h11.4a1.7 1.7 0 0 0 1.5-2.6L14 9.2V3" />
            <path d="M7.4 15h9.2" />
          </svg>
        </span>
        <div className="lab-card__title">
          <h3 id={headingId} className="body1 lab-card__heading">{LAB_HEADER.replace(/\.$/, '')}</h3>
          <span className="detail1 lab-muted">{meta}</span>
        </div>
        <span className={`detail1 lab-card__chip lab-tone--${model.outside ? 'outside' : 'in'}`}>
          {model.outside ? `${model.outside} of ${model.total} outside range` : 'All within range'}
        </span>
      </header>

      {model.note && <LabNoteBlock name={model.note.name} text={model.note.text} />}

      {open.map((group) => (
        <div key={group.key} className="lab-card__section">
          <p className="eyebrow lab-card__label">{group.label}</p>
          <ul className="lab-card__rows">
            {group.rows.map((row) => {
              const bar = labBar(row);
              const notes = notesOf(row);
              return (
                <li key={row.id} className="lab-row">
                  <div className="lab-row__name">
                    <span className="body2 lab-row__label">{row.name}</span>
                    {notes && <span className="detail1 lab-muted">{notes}</span>}
                  </div>
                  <div className="lab-row__value">
                    <span className="lab-row__num">{row.value}</span>
                    {row.unit && <span className="detail1 lab-muted">{row.unit}</span>}
                    <span className={`lab-flag lab-tone--${tone(row)}`}>{row.flag}</span>
                  </div>
                  <div className="lab-row__range">
                    {bar && (
                      <div className="lab-bar" aria-hidden="true" data-testid="lab-range-bar">
                        <span className="lab-bar__track" />
                        <span className="lab-bar__band" style={{ left: `${bar.bandFrom}%`, width: `${bar.bandTo - bar.bandFrom}%` }} />
                        <span className={`lab-bar__dot lab-bar__dot--${tone(row)}`} style={{ left: `${bar.at}%` }} />
                      </div>
                    )}
                    {row.range && <span className="detail1 lab-muted">{`Lab range: ${row.range}`}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {moreLabel && (
        <details className="lab-card__more">
          <summary className="body2">
            <span>{moreLabel}</span>
            <svg className="lab-card__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          {folded.map((group) => (
            <div key={group.key} className="lab-card__group">
              <p className="eyebrow lab-card__label">{group.label}</p>
              <ul className="lab-card__list">
                {group.rows.map((row) => {
                  const notes = notesOf(row);
                  return (
                    <li key={row.id} className="lab-item">
                      <span className="detail1 lab-item__name">
                        {row.name}
                        {notes && <span className="lab-muted">{` · ${notes}`}</span>}
                      </span>
                      <span className="detail1 lab-item__value">
                        {row.value}
                        {row.unit && <span className="lab-muted">{` ${row.unit}`}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {noValue.length > 0 && (
            <div className="lab-card__group">
              <p className="eyebrow lab-card__label">No value from the lab</p>
              <ul className="lab-card__list">
                {noValue.map((row) => (
                  <li key={row.id} className="lab-item">
                    <span className="detail1 lab-item__name">{row.name}</span>
                    <span className="detail1 lab-muted">{row.novalue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </details>
      )}

      {model.pending && <p className="detail1 lab-muted lab-card__note">{LAB_PENDING_LINE}</p>}
      {model.hidden > 0 && <p className="detail1 lab-muted lab-card__note">{labHiddenCopy(model.hidden)}</p>}

      <p className="detail1 lab-muted lab-card__note">
        {[LAB_FOOTER[0], model.outside ? LAB_OUTSIDE_NOTE : null, LAB_FOOTER[1]].filter(Boolean).join(' ')}
      </p>
    </article>
  );
}
